import * as THREE from "three";
import {
  App,
  CameraShake,
  FovTween,
  ProceduralBeat,
  SpringArmCamera,
  TempVec,
  math,
} from "@engine";
import { HUD } from "./ui/HUD";
import { InputMap } from "./input/InputMap";
import { Player } from "./player/Player";
import { WorldRotator } from "./world/WorldRotator";
import { SegmentFactory } from "./level/SegmentFactory";
import { LevelStreamer } from "./level/LevelStreamer";
import type { Shape } from "./level/types";
import { LEVELS } from "./level/levels";
import { WORLD_Y_OFFSET } from "./config/tunnel";
import { shapeOf } from "./config/shapes";
import { CameraBank } from "./fx/CameraBank";
import { PlayerPulse } from "./fx/PlayerPulse";
import { TileHighlight } from "./fx/TileHighlight";
import { GateTransition } from "./fx/GateTransition";
import { Sparks } from "./fx/Sparks";
import { PlayerTrail } from "./fx/PlayerTrail";
import { SkyTunnel } from "./fx/SkyTunnel";
import { ScoreSystem } from "./scoring/ScoreSystem";
import { BestScoresService } from "./services/BestScoresService";
import { ThemeService } from "./services/ThemeService";
import { EndlessGenerator } from "./level/EndlessGenerator";
import { dailyKey, dailySeed, mulberry32 } from "./level/seededRng";
import { PickupManager } from "./pickups/PickupManager";
import { MusicDirector } from "./audio/MusicDirector";
import { BeatTimingService } from "./audio/BeatTimingService";
import { FrenzyMode } from "./fx/FrenzyMode";
import { PulsePanelsFx } from "./fx/PulsePanelsFx";
import { MirrorTrickFx } from "./fx/MirrorTrickFx";
import { SweepFx } from "./fx/SweepFx";
import { BankingFx } from "./fx/BankingFx";
import { ReverseGateFx } from "./fx/ReverseGateFx";
import { StatsService } from "./services/StatsService";
import { AchievementService } from "./services/AchievementService";
import { CosmeticsService } from "./services/CosmeticsService";
import { DifficultyService } from "./services/DifficultyService";
import { StoryProgressService } from "./services/StoryProgressService";
import { AchievementToast } from "./ui/AchievementToast";
import { StatsScreen } from "./ui/StatsScreen";
import { LevelSelectScreen } from "./ui/LevelSelectScreen";
import {
  describeModifier,
  pickModifierForDay,
  type DailyModifierId,
} from "./level/dailyModifiers";
import {
  DAILY_THEMES,
  ENDLESS_THEMES,
  STORY_THEMES,
  themeById,
} from "./config/themes";

const ENDLESS_TOPUP_THRESHOLD = 4;
const TUTORIAL_STORAGE_KEY = "morph.tutorialSeen";

// Maps combo count to PlayerTrail tier index.
//   combo 0..2  -> 0 (white)
//   combo 3..5  -> 1 (cyan)
//   combo 6..8  -> 2 (pink)
//   combo 9+    -> 3 (gold)
function comboTier(combo: number): number {
  if (combo >= 9) return 3;
  if (combo >= 6) return 2;
  if (combo >= 3) return 1;
  return 0;
}

type GameState =
  | "loading"
  | "menu"
  | "playing"
  | "paused"
  | "success"
  | "dead"
  | "stats"
  | "story-select";
type GameMode = "story" | "endless" | "daily";

const SUCCESS_HOLD_MS = 1200;

const SHAKE_BASE = 0.01;
const SHAKE_PER_SPEED = 0.008;
const SHAKE_MAX = 0.18;
const FOV_BASE = 75;
const FOV_BOOST = 105;
// Bloom envelope -- intentionally restrained so the scene reads as
// "moody, neon-edged" rather than "blown out". Drops + gates still
// punch through via the audio + gateBoost terms; see the bloom write
// in update() for the full equation.
const BLOOM_BASE = 0.32;
const BLOOM_MAX = 1.0;
const BLOOM_PER_AUDIO_HIGH = 0.85;
const BPM_MIN = 120;
const BPM_MAX = 160;
const CA_BASE = 0.0008;
const CA_PER_SPEED_T = 0.002;
const CA_DEATH_PEAK = 0.025;
const CA_DEATH_DECAY_LAMBDA = 4;
const DOLLY_BASE_Z = 6.5;

export class MorphHexGame {
  private app: App;
  private hud: HUD;
  private inputMap: InputMap;
  private player: Player;
  private rotator: WorldRotator;
  private factory: SegmentFactory;
  private streamer: LevelStreamer;
  private springArm: SpringArmCamera;
  private shake: CameraShake;
  private fovTween: FovTween;
  private beat: ProceduralBeat;

  private cameraBank: CameraBank;
  private playerPulse: PlayerPulse;
  private tileHighlight: TileHighlight;
  private gateTransition: GateTransition;
  private sparks: Sparks;
  private trail: PlayerTrail;
  private skyTunnel: SkyTunnel;
  private score: ScoreSystem;
  private bests: BestScoresService;
  private themes: ThemeService;
  private pickups: PickupManager;
  private musicDirector: MusicDirector;
  private beatTiming: BeatTimingService;
  private frenzy: FrenzyMode;
  private pulsePanels: PulsePanelsFx;
  private mirrorFx: MirrorTrickFx;
  private sweepFx: SweepFx;
  private bankingFx: BankingFx;
  private reverseGate: ReverseGateFx;
  private stats: StatsService;
  private achievements: AchievementService;
  private cosmetics: CosmeticsService;
  private difficulty: DifficultyService;
  private storyProgress: StoryProgressService;
  private toast: AchievementToast;
  private statsScreen: StatsScreen;
  private levelSelect: LevelSelectScreen;
  private endlessGenerator: EndlessGenerator | null = null;
  private endlessThemeIdx = 0;
  // Theme id we last applied for an endless run. Cached so that a
  // death-restart can re-apply the SAME theme instead of cycling to
  // the next slot in the rotation. null until the first endless run.
  private lastEndlessThemeId: string | null = null;
  // Captured at startGame for daily mode so onDeath / state
  // transitions can read the modifier without having to recompute it.
  private currentDailyModifier: DailyModifierId | null = null;
  // Track which shapes the current run has visited so we can bump the
  // shape-set stat on the first segment of each new shape. Reset in
  // startGame.
  private shapesVisitedThisRun = new Set<string>();

  private state: GameState = "loading";
  private mode: GameMode = "story";
  private bloomEMA = BLOOM_BASE;
  private cameraTarget: THREE.Object3D;
  private deathTimer = 0;
  private deathCAPulse = 0;
  private dollyOffset = new THREE.Vector3(0, 1.4, DOLLY_BASE_Z);
  private currentLevelIndex = 0;
  private successTimer: number | null = null;
  private resumedFromState: GameState = "playing";

  constructor(app: App) {
    this.app = app;
    this.hud = new HUD();

    this.app.scene.worldGroup.position.y = WORLD_Y_OFFSET;
    this.app.scene.worldGroup.position.z = 0;

    this.app.assets.setAudioContextFactory(() => this.app.audio.context);

    this.inputMap = new InputMap(this.app.input);

    this.player = new Player(this.app.physics);
    this.app.scene.scene.add(this.player.mesh);

    this.factory = new SegmentFactory(this.app.assets, this.app.physics);
    this.streamer = new LevelStreamer(this.factory, this.app.scene.worldGroup);
    this.rotator = new WorldRotator(this.app.scene.worldGroup, this.app.physics);

    this.cameraTarget = new THREE.Object3D();
    this.cameraTarget.position.set(0, 0.3, 0);
    this.app.scene.scene.add(this.cameraTarget);

    this.springArm = new SpringArmCamera(this.app.scene.camera, {
      offset: new THREE.Vector3(0, 1.4, DOLLY_BASE_Z),
      positionLambda: 9,
      rotationLambda: 11,
    });
    this.springArm.setTarget(this.cameraTarget);
    this.springArm.snapToTarget();

    this.shake = new CameraShake();
    this.shake.setMaxOffset(SHAKE_MAX);
    this.fovTween = new FovTween(this.app.scene.camera);
    this.fovTween.setImmediate(FOV_BASE);

    this.beat = new ProceduralBeat(this.app.audio, { bpm: BPM_MIN });

    this.cameraBank = new CameraBank();
    this.playerPulse = new PlayerPulse(this.player);
    this.tileHighlight = new TileHighlight(
      this.factory,
      this.streamer,
      this.rotator
    );
    this.gateTransition = new GateTransition(this.app, this.beat, this.hud);
    this.sparks = new Sparks();
    this.sparks.attach(this.app.scene.scene);
    this.trail = new PlayerTrail();
    this.trail.attach(this.app.scene.scene);
    this.skyTunnel = new SkyTunnel(this.app.quality.settings.tier);
    this.skyTunnel.attach(this.app.scene.scene);
    const initSize = this.app.renderer.getSize();
    this.skyTunnel.setAspect(initSize.width, initSize.height);
    this.app.renderer.onResize.on(({ width, height }) => {
      this.skyTunnel.setAspect(width, height);
    });
    this.score = new ScoreSystem(this.streamer, this.player, this.rotator, this.hud);
    this.bests = new BestScoresService();
    this.themes = new ThemeService(this.app.scene, this.factory);
    this.themes.setSkyTunnel(this.skyTunnel);
    this.pickups = new PickupManager(
      this.app,
      this.player,
      this.score,
      this.hud,
      this.sparks,
      this.factory,
      this.streamer
    );
    this.musicDirector = new MusicDirector(
      this.app,
      this.beat,
      this.themes,
      this.score,
      this.streamer,
      this.player,
      this.app.quality.settings
    );

    // Variety + skill-loop systems. Order matters: BeatTimingService
    // subscribes to beat.onStep so it must be built after the beat;
    // FrenzyMode subscribes to score.onComboChanged so the score
    // system must exist; the trick FX systems subscribe to streamer
    // events / beat steps that are already live.
    this.beatTiming = new BeatTimingService(this.beat);
    this.frenzy = new FrenzyMode(
      this.score,
      this.player,
      this.hud,
      this.sparks,
      this.beat,
      this.musicDirector
    );
    this.pulsePanels = new PulsePanelsFx(this.factory, this.beat);
    this.mirrorFx = new MirrorTrickFx(this.streamer, this.hud);
    this.sweepFx = new SweepFx(this.streamer, this.beat);
    this.bankingFx = new BankingFx(this.streamer, this.cameraBank);
    this.reverseGate = new ReverseGateFx(
      this.streamer,
      this.rotator,
      this.player,
      this.cameraBank,
      this.mirrorFx
    );

    // Progression: stats are the source of truth for achievements,
    // achievements gate cosmetics, cosmetics paint the player + trail.
    // The toast surface is purely UI; StatsScreen is the menu overlay.
    this.stats = new StatsService();
    this.achievements = new AchievementService(this.stats);
    this.cosmetics = new CosmeticsService(this.achievements);
    this.difficulty = new DifficultyService();
    this.storyProgress = new StoryProgressService();
    this.toast = new AchievementToast();
    this.statsScreen = new StatsScreen(
      this.stats,
      this.achievements,
      this.cosmetics
    );
    this.levelSelect = new LevelSelectScreen(
      this.storyProgress,
      this.difficulty,
      this.bests
    );

    this.wireSignals();
    this.app.addSystem({
      update: (dt) => this.update(dt),
    });

    this.app.assets.progress.on(({ loaded, total }) => {
      this.hud.setLoading(total === 0 ? 1 : loaded / total);
    });

    this.app.scene.scene.environment = null;
  }

  private wireSignals(): void {
    this.inputMap.onStart.on(() => {
      // Default any-key from the menu opens the story Level-Select sub-
      // screen (preserves the original "press any key to play" feel
      // while still respecting the per-difficulty unlocks).
      if (this.state === "menu") this.openStorySelectScreen();
    });
    this.inputMap.onRestart.on(() => {
      // Restart from the dead screen returns to whatever mode the run
      // was in. For story mode we preserve currentLevelIndex so the
      // player resumes the level they died on (matching what the
      // Level-Select sub-screen booted them into) instead of being
      // bounced back to level 1.
      if (this.state === "dead" && this.deathTimer > 0.4) {
        this.startGame(this.mode, this.currentLevelIndex);
      }
    });
    this.inputMap.onPickMode.on((mode) => {
      if (this.state !== "menu") return;
      // Mirror the on-screen menu buttons: keyboard "1" opens the
      // story level-select rather than booting level 1 directly, so
      // the keyboard shortcut respects the per-difficulty unlocks.
      if (mode === "story") this.openStorySelectScreen();
      else this.startGame(mode);
    });
    this.hud.onMenuAction.on((mode) => {
      if (this.state !== "menu") return;
      // Story routes through the level-select sub-screen so the
      // player picks a level (gated by the per-difficulty unlock
      // counter) before the run actually starts. Endless / daily
      // stay one-tap for fast access.
      if (mode === "story") this.openStorySelectScreen();
      else this.startGame(mode);
    });
    this.hud.onMenuExtra.on((act) => {
      if (act === "stats" && this.state === "menu") this.openStatsScreen();
    });
    this.hud.onStatsAction.on((act) => {
      if (act === "back" && this.state === "stats") this.closeStatsScreen();
    });
    this.hud.onStoryAction.on((act) => {
      if (act === "back" && this.state === "story-select") {
        this.closeStorySelectScreen();
      }
    });
    // Level card click on the Story Level-Select screen. Cards already
    // gate themselves on isUnlocked, so we can trust the emitted level
    // is playable. startGame seeds currentLevelIndex from the picked
    // 1-indexed level.
    this.levelSelect.onSelect.on((level1) => {
      if (this.state !== "story-select") return;
      this.startGame("story", level1 - 1);
    });
    // Difficulty pill row in the menu. Persist the choice immediately;
    // re-paint the active pill so the player sees their selection
    // confirm. Setting mid-run isn't supported (we only re-read the
    // profile at startGame), so the pill row is hidden by HudState.
    this.hud.onDifficulty.on((id) => {
      this.difficulty.setLevel(id);
      this.hud.setDifficultyActive(id);
    });
    // Achievement unlocks: show a toast immediately, regardless of
    // whether we're mid-run or in the menu. The toast pool itself
    // handles overlap.
    this.achievements.onUnlocked.on((def) => this.toast.enqueue(def));
    this.inputMap.onTogglePause.on(() => {
      if (this.state === "playing") this.pauseGame();
      else if (this.state === "paused") this.resumeGame();
    });
    this.inputMap.onRotateLeft.on(() => {
      if (this.state === "playing") this.handleRotate(1);
    });
    this.inputMap.onRotateRight.on(() => {
      if (this.state === "playing") this.handleRotate(-1);
    });
    this.hud.onPauseAction.on((act) => {
      if (this.state !== "paused") return;
      if (act === "resume") this.resumeGame();
      else if (act === "restart") this.restartFromPause();
      else if (act === "menu") this.quitToMenu();
    });
    this.app.onResume.on(() => {
      // Tab visibility can call app.resume() unsolicited (e.g. user
      // tab-switches while their pause overlay is up). Re-suspend the
      // loop so the user keeps control of when play resumes.
      if (this.state === "paused") this.app.pause();
    });

    this.rotator.onRotated.on((e) => {
      this.cameraBank.bank(e.direction);
      this.playerPulse.onRotate(e.direction);
    });

    this.player.onWallContact.on(() => {
      const color = shapeOf(this.streamer.getCurrentShape()).primary;
      this.sparks.burstAt(this.player.mesh.position, color, 10);
    });
    this.player.onDied.on(() => this.onDeath());

    // Last-chance save: rotate just before a lethal hit converts it to
    // a graze-with-shake. The combo still resets so the save isn't a
    // free pass, but the run continues.
    this.player.onLastChanceSave.on(() => {
      this.hud.spawnPopup("CLOSE!", "warm");
      const color = shapeOf(this.streamer.getCurrentShape()).primary;
      this.sparks.burstAt(this.player.mesh.position, color, 22);
      this.score.combo = 0;
      this.score.multiplier = 1;
      this.score.perfectStreak = 0;
      this.hud.setCombo(0, 1);
      this.stats.bumpSave();
    });

    // Stats: per-rotation perfect bumps + combo/streak watermarks.
    this.score.onPerfectRotation.on(() => this.stats.bumpPerfect());
    this.score.onComboChanged.on((combo) =>
      this.stats.noteCombo(combo, this.score.perfectStreak)
    );
    this.frenzy.onActivated.on(() => this.stats.bumpFrenzy());
    // Power-pickup counters: route every magnet/double consume into
    // StatsService. shield/slowmo/score don't have lifetime counters
    // (they'd duplicate combo / shield-saved telemetry), so we filter.
    this.pickups.onPickupConsumed.on((ev) => {
      if (ev.type === "magnet") this.stats.bumpMagnet();
      else if (ev.type === "double") this.stats.bumpDouble();
    });

    // Schedule the HUD beat dot pulse to land EXACTLY when the audio
    // hit is heard. ProceduralBeat schedules each step ~150ms ahead of
    // audio time, so we delay the visual to match. Cheap setTimeout
    // per kick/snare; shorter delay is fine because stale timeouts
    // simply double-class an element that's already mid-pulse and
    // pulseBeat() debounces the reset timer.
    this.beat.onStep.on((step) => {
      const ctx = this.app.audio.context;
      if (!ctx) return;
      const delayMs = Math.max(0, (step.time - ctx.currentTime) * 1000);
      if (step.isKick) {
        window.setTimeout(() => this.hud.pulseBeat("kick"), delayMs);
      } else if (step.isSnare) {
        window.setTimeout(() => this.hud.pulseBeat("snare"), delayMs);
      }
    });

    // Boss intro / outro hooks. Music director cues a riser on the
    // next bar boundary and pins intensity for ~12s so the boss
    // bar lands hot. HUD shows the named banner.
    this.streamer.onBossStart.on((e) => {
      this.hud.setBossBanner(e.marker.banner);
      this.musicDirector.cueBoss();
      this.musicDirector.setIntensityBoost(1.2, 12);
    });
    this.streamer.onBossCleared.on((e) => {
      this.hud.setBossBanner(null);
      this.score.addBonus(500);
      this.hud.spawnPopup("BOSS CLEAR +500", "hot");
      this.hud.flash("gate", "#7fffaa");
      // Stats: only credit the bossesById set on actual clears (so a
      // mid-boss death doesn't unlock the "all four bosses" achievement).
      this.stats.bumpBoss(e.marker.id);
      // Apex Gate reuses the reverse_gate marker.id to engage the
      // gravity-flip FX without forking it, so we disambiguate by
      // banner text. Bumping a synthetic "apex_gate" boss id powers
      // the apex_clear achievement predicate.
      if (e.marker.banner === "BOSS: APEX GATE") {
        this.stats.bumpBoss("apex_gate");
      }
    });

    this.streamer.onShapeChanged.on((shape) => {
      this.hud.setShape(shape);
      this.applyShape(shape);
      // Track unique shapes per run for the ALL_SHAPES achievement.
      // shapesVisitedThisRun is reset in startGame; the StatsService
      // also dedupes internally so a re-emit on the same shape is a
      // cheap no-op.
      if (!this.shapesVisitedThisRun.has(shape)) {
        this.shapesVisitedThisRun.add(shape);
        this.stats.noteShapeVisited(shape);
      }
    });
    this.streamer.onSpeedChanged.on((speed) => {
      const t = math.clamp((speed - 10) / 10, 0, 1);
      this.fovTween.tweenTo(FOV_BASE + (FOV_BOOST - FOV_BASE) * t, 0.6);
      this.beat.setBpm(BPM_MIN + (BPM_MAX - BPM_MIN) * t);
    });
    this.streamer.onGateCrossed.on(({ toShape }) => {
      this.gateTransition.trigger(toShape);
      const burstPos = TempVec.vec3().set(0, 0, 0);
      this.sparks.gateBurst(burstPos, shapeOf(toShape).primary);
    });
    this.streamer.onLevelComplete.on(() => {
      this.onSuccess();
    });
  }

  async preload(): Promise<void> {
    await this.app.assets.loadAll();
  }

  showMenu(): void {
    this.state = "menu";
    this.mode = "story";
    this.hud.setState("menu");
    this.currentLevelIndex = 0;
    // setLevel() detaches every active segment, so any panel mesh the
    // tile highlight was tracking is about to be removed from the
    // scene. Drop the reference cleanly before that happens.
    this.tileHighlight.clear();
    this.clearVarietyFx();
    this.streamer.setLevel(LEVELS[0]!);
    const initialShape = this.streamer.getCurrentShape();
    this.applyShape(initialShape, false);
    this.hud.setShape(initialShape);
    this.hud.setLevel(1);
    this.fovTween.setImmediate(FOV_BASE);
    this.inputMap.setGameplayActive(false);
    this.refreshMenuBests();
    // Reset to neutral palette so the menu always looks the same.
    this.themes.apply(themeById("neon"));
    // Drop any leftover pickup state and quiet the melodic layers; the
    // drum loop keeps running so the menu still has its signature beat.
    this.pickups.reset();
    this.musicDirector.silence(200);
    // Paint the active difficulty pill from the persisted selection so
    // the player sees their last-chosen tier highlighted on every menu
    // entry (boot + quitToMenu + closeStatsScreen).
    this.hud.setDifficultyActive(this.difficulty.getId());
  }

  // Wipe Frenzy + Mirror + Boss banner state. Runs on every state
  // transition that recycles the streamer (showMenu, startGame,
  // advanceLevel, onDeath) so a fresh run never inherits leftover
  // visuals.
  private clearVarietyFx(): void {
    this.frenzy.clear();
    this.mirrorFx.clear();
    this.pulsePanels.reset();
    this.bankingFx.clear();
    this.reverseGate.clear();
    this.hud.setBossBanner(null);
    this.hud.setMirrorIndicator(false);
    this.hud.setFrenzy(false);
    this.hud.setDailyModifierBanner(null);
  }

  private refreshMenuBests(): void {
    const endlessBest = this.bests.get("endless")?.score ?? null;
    const todayKey = dailyKey();
    const dailyBest = this.bests.get(`daily:${todayKey}`)?.score ?? null;
    // Include the active modifier in the menu sub-label so the player
    // knows what they're signing up for before they hit Daily. The
    // pick is deterministic for the day, so this is stable across
    // refreshes.
    const modifier = pickModifierForDay(todayKey);
    const base =
      dailyBest != null
        ? `${todayKey} - ${dailyBest.toLocaleString()}`
        : todayKey;
    const dailyLabel = `${base} - ${modifier.banner}`;
    this.hud.setMenuBests(endlessBest, dailyLabel);
  }

  // startLevelIdx0 (story-mode only) seeds currentLevelIndex so the
  // Level-Select sub-screen can boot any unlocked level. Endless /
  // daily ignore it. Defaulting to 0 keeps the existing menu-tap and
  // death-restart flows on level 1 / the player's current level
  // respectively.
  startGame(mode: GameMode = "story", startLevelIdx0 = 0): void {
    this.cancelSuccessTimer();
    // Capture the previous state BEFORE flipping to "playing" so the
    // theme picker can tell a fresh attempt (from menu) apart from a
    // death-restart. Death-restart should keep the same endless theme
    // so the player isn't yanked into different lighting/colors every
    // time they crash mid-attempt.
    const fromDeath = this.state === "dead" && this.mode === mode;
    this.mode = mode;
    this.state = "playing";
    this.hud.setState("game");
    this.hud.setDeadTitle("CRASHED", "death");
    this.player.reset();
    this.rotator.reset();
    // Story-mode boots at the picked level (defaults to 0). For other
    // modes this index is meaningless; we still reset it so a death
    // bounce from story leaves no residue.
    this.currentLevelIndex =
      mode === "story"
        ? Math.max(0, Math.min(LEVELS.length - 1, startLevelIdx0))
        : 0;
    // Drop highlight before loadModeLevel() recycles every segment, or
    // we'd briefly carry a pointer to a detached panel mesh.
    this.tileHighlight.clear();
    this.clearVarietyFx();
    // Per-run stats baseline + apply the player's cosmetic selection
    // BEFORE loadModeLevel() so the visuals are in place when the
    // streamer attaches the first segment. shapesVisitedThisRun is
    // cleared here so the next run gets a fresh ALL_SHAPES window.
    this.stats.beginRun();
    this.shapesVisitedThisRun.clear();
    this.cosmetics.apply(this.player, this.trail);
    this.toast.clear();
    // Apply the active difficulty profile BEFORE loadModeLevel() so the
    // EndlessGenerator and SegmentFactory both see the correct
    // multipliers from the very first segment. Player gets the scaled
    // last-chance window; PickupManager owns the score reward mul
    // because it composes with the double-pickup window.
    const profile = this.difficulty.getProfile();
    this.player.setLastChanceWindow(0.14 * profile.lastChanceMul);
    this.pickups.setDifficultyScoreMul(profile.scoreMul);
    // Re-paint the in-game DIFF chip in case the player flipped from
    // Easy to Hard between runs (the chip element is owned by HUD; we
    // call setDifficultyActive to update both menu pill and chip in
    // one place).
    this.hud.setDifficultyActive(profile.id);
    // endlessThemeIdx only advances on fresh menu-initiated runs; a
    // death-restart re-uses the previous theme so the visual world
    // doesn't keep re-skinning underneath the player. Daily is
    // deterministic from the date seed so the theme picker ignores
    // this counter regardless.
    this.loadModeLevel(fromDeath);
    this.score.reset();
    this.gateTransition.reset();
    this.cameraBank.reset();
    this.playerPulse.reset();
    this.deathCAPulse = 0;
    const initialShape = this.streamer.getCurrentShape();
    this.applyShape(initialShape, true);
    this.hud.setShape(initialShape);
    this.applyHudModeLabel();
    // Make sure freshly-spawned wall bodies are placed at their correct
    // world positions BEFORE the first physics.step, otherwise the player
    // (at world origin) would briefly overlap every wall body's default
    // (0,0,0) position and get launched.
    this.streamer.syncBodies();
    this.fovTween.tweenTo(FOV_BASE + 6, 0.4);
    this.beat.setBpm(BPM_MIN);
    this.inputMap.setGameplayActive(true);
    this.deathTimer = 0;
    this.pickups.reset();
    this.musicDirector.restoreFromCombo();
    this.maybeShowTutorial();
    void this.app.audio.start().then(() => {
      if (!this.beat.isPlaying()) void this.beat.start();
      // MusicDirector needs the AudioContext to exist. start() is idempotent.
      this.musicDirector.start();
    });
  }

  // Loads the appropriate level data for the current mode and updates
  // streamer flags. Story uses the hand-authored LEVELS array; Endless
  // and Daily seed an EndlessGenerator and feed its initial batch into
  // the streamer, then flip the streamer into endless mode so it never
  // recycles the last segment or fires onLevelComplete.
  //
  // `keepEndlessTheme`: when true (death-restart), the endless theme
  // index is not advanced; the player keeps the same look they had
  // last attempt. Otherwise (menu-initiated start), the rotation
  // advances so consecutive new attempts feel visually distinct.
  private loadModeLevel(keepEndlessTheme = false): void {
    if (this.mode === "story") {
      this.endlessGenerator = null;
      this.currentDailyModifier = null;
      this.streamer.setLevel(LEVELS[this.currentLevelIndex]!);
      this.streamer.setEndless(false);
    } else {
      this.endlessGenerator = this.buildEndlessGenerator();
      this.streamer.setLevel(this.endlessGenerator.initialBatch());
      this.streamer.setEndless(true);
    }
    this.applyThemeForCurrent(keepEndlessTheme);
    this.applyDailyModifierEffects();
  }

  // Picks the theme for the current mode + level and applies it. Story:
  // STORY_THEMES indexed by level. Endless: rotates through ENDLESS_THEMES
  // on fresh menu-initiated runs; reuses the cached lastEndlessThemeId
  // on death-restart so a player retrying mid-attempt isn't yanked into
  // a different palette every crash. Daily: deterministic pick from the
  // date seed.
  private applyThemeForCurrent(keepEndlessTheme = false): void {
    let themeId: string;
    if (this.mode === "story") {
      themeId = STORY_THEMES[this.currentLevelIndex] ?? "neon";
    } else if (this.mode === "endless") {
      if (keepEndlessTheme && this.lastEndlessThemeId) {
        themeId = this.lastEndlessThemeId;
      } else {
        themeId = ENDLESS_THEMES[this.endlessThemeIdx % ENDLESS_THEMES.length]!;
        this.endlessThemeIdx += 1;
        this.lastEndlessThemeId = themeId;
      }
    } else {
      // Daily: use the date seed to deterministically pick a theme.
      const seed = dailySeed(dailyKey());
      themeId = DAILY_THEMES[seed % DAILY_THEMES.length]!;
    }
    this.themes.apply(themeById(themeId));
  }

  private buildEndlessGenerator(): EndlessGenerator {
    const profile = this.difficulty.getProfile();
    if (this.mode === "daily") {
      // Seed the generator with today's date so every player gets the
      // same level layout. Note: only level layout is seeded; particle
      // / shake RNG stays Math.random because visual noise doesn't
      // affect run "fairness". The daily modifier is also captured here
      // so onDeath / HUD can read it without recomputing.
      const key = dailyKey();
      const modifier = pickModifierForDay(key);
      this.currentDailyModifier = modifier.id;
      return new EndlessGenerator(
        mulberry32(dailySeed(key)),
        modifier.id,
        profile
      );
    }
    this.currentDailyModifier = null;
    return new EndlessGenerator(Math.random, null, profile);
  }

  // Apply the side-effects of the active daily modifier that live
  // OUTSIDE the EndlessGenerator (music / pickups / HUD banner). The
  // EndlessGenerator already consumes the modifier id at construction
  // time, so this method only handles the cross-system flips.
  private applyDailyModifierEffects(): void {
    // Reset the per-modifier overrides every time so a re-run from a
    // non-daily mode never inherits old caps.
    this.musicDirector.setLayerCap(null);
    this.pickups.setAllowedTypes(null);
    if (this.mode !== "daily" || !this.currentDailyModifier) {
      this.hud.setDailyModifierBanner(null);
      return;
    }
    const modifier = this.currentDailyModifier;
    if (modifier === "sub_only") {
      this.musicDirector.setLayerCap("sub");
    } else if (modifier === "shield_off") {
      // Shield orbs may still spawn; we filter them at consume time.
      this.pickups.setAllowedTypes(["score", "slowmo"]);
    }
    const def = describeModifier(modifier);
    this.hud.setDailyModifierBanner(def.banner);
    // Stats: this is the moment a player "played" today's daily.
    // StatsService dedupes by date so re-runs on the same day are
    // counted once.
    this.stats.noteDailyPlayed(dailyKey());
  }

  private maybeShowTutorial(): void {
    if (this.tutorialSeen()) {
      this.hud.hideTutorial();
      return;
    }
    this.hud.showTutorial();
  }

  private tutorialSeen(): boolean {
    try {
      return localStorage.getItem(TUTORIAL_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  }

  private markTutorialSeen(): void {
    try {
      localStorage.setItem(TUTORIAL_STORAGE_KEY, "1");
    } catch {
      // ignore storage errors
    }
  }

  private dismissTutorialIfShown(): void {
    if (this.tutorialSeen()) return;
    this.markTutorialSeen();
    this.hud.hideTutorial();
  }

  private applyHudModeLabel(): void {
    if (this.mode === "story") {
      this.hud.setLevel(this.currentLevelIndex + 1);
    } else if (this.mode === "endless") {
      this.hud.setModeLabel("ENDLESS");
    } else {
      this.hud.setModeLabel("DAILY");
    }
  }

  private onSuccess(): void {
    if (this.state !== "playing") return;
    this.state = "success";
    this.inputMap.setGameplayActive(false);
    const distance = this.streamer.getDistance();
    const score = this.score.score;
    const combo = this.score.combo;
    // Persist per-run totals (lifetime distance/score/runs/longest*).
    // For story we credit at every cleared level; for endless / daily
    // we credit on death. recordRunEnded is idempotent inside the
    // service for the per-run counter resets handled by beginRun().
    this.stats.recordRunEnded({
      distance,
      score,
      combo,
      perfectStreak: this.score.perfectStreak,
      difficulty: this.difficulty.getId(),
    });
    const result = this.bests.submit(this.currentBestKey(), {
      score,
      distance,
      combo,
    });
    // Story-mode unlock: clearing level N on a difficulty unlocks N+1
    // for that same difficulty (per-diff progression). Capped inside
    // the service at LEVELS.length so beating the final level just
    // leaves the counter at its terminal value.
    if (this.mode === "story") {
      this.storyProgress.markCleared(
        this.difficulty.getId(),
        this.currentLevelIndex + 1
      );
    }
    this.hud.flash("gate", result.isNewBest ? "#ffd23f" : "#7fffaa");
    this.hud.setSuccess(
      this.currentLevelIndex + 1,
      distance,
      score,
      result.isNewBest
    );
    if (result.isNewBest) this.hud.spawnPopup("NEW BEST", "hot");
    this.hud.setState("success");
    this.fovTween.tweenTo(FOV_BASE - 4, 0.4);
    this.cancelSuccessTimer();
    this.successTimer = window.setTimeout(
      () => this.advanceLevel(),
      SUCCESS_HOLD_MS
    );
  }

  private advanceLevel(): void {
    this.successTimer = null;
    // Endless / daily never trigger onSuccess (the streamer fires
    // onLevelComplete only when its hand-authored level is exhausted),
    // so this path is story-only — but guard anyway for safety.
    if (this.mode !== "story") {
      this.state = "playing";
      this.hud.setState("game");
      this.inputMap.setGameplayActive(true);
      return;
    }
    this.currentLevelIndex += 1;
    if (this.currentLevelIndex >= LEVELS.length) {
      // Whole game cleared. Show the dead screen as a victory celebration
      // so the existing "tap to retry" flow restarts the run.
      this.state = "dead";
      this.hud.setDeadTitle("GAME CLEAR", "victory");
      this.hud.setFinalDistance(this.streamer.getDistance());
      this.hud.setFinalScore(this.score.score);
      this.hud.setState("dead");
      this.currentLevelIndex = 0;
      this.deathTimer = 0;
      return;
    }
    this.state = "playing";
    this.hud.setState("game");
    this.tileHighlight.clear();
    this.clearVarietyFx();
    this.streamer.setLevel(LEVELS[this.currentLevelIndex]!);
    this.applyThemeForCurrent();
    const initialShape = this.streamer.getCurrentShape();
    this.applyShape(initialShape, true);
    this.hud.setShape(initialShape);
    this.applyHudModeLabel();
    this.streamer.syncBodies();
    this.beat.setBpm(BPM_MIN);
    this.gateTransition.reset();
    this.fovTween.tweenTo(FOV_BASE + 6, 0.3);
    this.inputMap.setGameplayActive(true);
  }

  private onDeath(): void {
    if (this.state !== "playing") return;
    this.cancelSuccessTimer();
    this.state = "dead";
    this.inputMap.setGameplayActive(false);
    const distance = this.streamer.getDistance();
    const score = this.score.score;
    // Stats: record the run regardless of mode so lifetime totals
    // include story attempts that ended in a crash. The success path
    // will also call recordRunEnded for per-level clears -- the
    // service simply increments runs/totals each time it's called.
    this.stats.recordRunEnded({
      distance,
      score,
      combo: this.score.combo,
      perfectStreak: this.score.perfectStreak,
      difficulty: this.difficulty.getId(),
    });
    // For endless / daily, death IS the moment to record the run; story
    // mode records on success (per-level) so a story death is purely a
    // restart prompt with no best-score comparison.
    let isNewBest = false;
    if (this.mode === "endless" || this.mode === "daily") {
      const result = this.bests.submit(this.currentBestKey(), {
        score,
        distance,
        combo: this.score.combo,
      });
      isNewBest = result.isNewBest;
      if (isNewBest) this.hud.spawnPopup("NEW BEST", "hot");
    }
    this.hud.setDeadTitle("CRASHED", "death", isNewBest);
    this.hud.flash("death");
    this.hud.setFinalDistance(distance);
    this.hud.setFinalScore(score);
    this.hud.setState("dead");
    this.fovTween.tweenTo(FOV_BASE - 6, 0.6);
    this.sparks.deathBurst(this.player.mesh.position);
    this.deathCAPulse = CA_DEATH_PEAK;
    this.deathTimer = 0;
    // Wipe shield + slow-mo so a respawn never inherits leftover buffs.
    // MusicDirector also subscribes to onDied directly to silence layers.
    this.pickups.reset();
    this.clearVarietyFx();
  }

  // Best-score key includes the difficulty so each tier has its own
  // leaderboard. Story keys also include the difficulty so a Hard
  // run's per-level best doesn't get clobbered by an Easy run.
  // Daily keys include the date already; we add the difficulty after.
  private currentBestKey(): string {
    const diff = this.difficulty.getId();
    switch (this.mode) {
      case "story":
        return `story:${this.currentLevelIndex + 1}:${diff}`;
      case "endless":
        return `endless:${diff}`;
      case "daily":
        return `daily:${dailyKey()}:${diff}`;
    }
  }

  private cancelSuccessTimer(): void {
    if (this.successTimer !== null) {
      window.clearTimeout(this.successTimer);
      this.successTimer = null;
    }
  }

  private pauseGame(): void {
    if (this.state !== "playing") return;
    this.resumedFromState = this.state;
    this.state = "paused";
    this.inputMap.setGameplayActive(false);
    this.hud.setState("paused");
    // Silence melodic layers so the pause overlay doesn't have a rich
    // sustained mix bleeding under it. Drums halt with the rAF/audio.
    this.musicDirector.silence(150);
    // App.pause stops the rAF loop and suspends audio so the world is
    // truly frozen — physics, render, beat all halt. Resume re-attaches
    // them.
    this.app.pause();
  }

  private resumeGame(): void {
    if (this.state !== "paused") return;
    this.state = this.resumedFromState;
    this.app.resume();
    this.hud.setState("game");
    this.inputMap.setGameplayActive(true);
    this.musicDirector.restoreFromCombo();
  }

  private restartFromPause(): void {
    if (this.state !== "paused") return;
    this.app.resume();
    // Preserve mode + (for story) the current level so "Restart" from
    // the pause overlay actually re-runs the level the player is on,
    // instead of silently dropping them onto story level 1 regardless
    // of which mode they were playing.
    this.startGame(this.mode, this.currentLevelIndex);
  }

  private quitToMenu(): void {
    if (this.state !== "paused") return;
    this.app.resume();
    this.showMenu();
  }

  // The stats screen is a passive overlay -- the world keeps rendering
  // (menu camera + slow streamer drift) underneath. We swap the HUD
  // state so the menu's own controls are hidden, then re-render the
  // panes so any newly-unlocked achievements / cosmetics show up
  // immediately even if the player just earned them in the prior run.
  private openStatsScreen(): void {
    if (this.state !== "menu") return;
    this.state = "stats";
    this.hud.setState("stats");
    this.statsScreen.show();
  }

  private closeStatsScreen(): void {
    if (this.state !== "stats") return;
    // showMenu also resets streamer/level state, but only
    // tileHighlight and the streamer mutate -- the menu's own bests
    // get refreshed there. Cheap enough to call unconditionally.
    this.showMenu();
  }

  // Story Level-Select sub-screen. Mirrors openStatsScreen: same
  // overlay pattern (world keeps rendering underneath, HUD state
  // swap hides the menu controls). The LevelSelectScreen subscribes
  // to DifficultyService.onChanged so flipping a pill inside the
  // sub-screen re-renders the lock state without us doing anything.
  private openStorySelectScreen(): void {
    if (this.state !== "menu") return;
    this.state = "story-select";
    this.hud.setState("story-select");
    // Make sure the difficulty pill row inside the sub-screen reflects
    // the persisted selection (the constructor binds the row but we
    // paint the active class only via setDifficultyActive).
    this.hud.setDifficultyActive(this.difficulty.getId());
    this.levelSelect.show();
  }

  private closeStorySelectScreen(): void {
    if (this.state !== "story-select") return;
    this.levelSelect.hide();
    this.showMenu();
  }

  private applyShape(shape: Shape, snap = false): void {
    this.rotator.setShape(shape, !snap);
  }

  // Wraps every rotate input so we can:
  //   * Judge timing against the beat for PERFECT/GOOD bonuses.
  //   * Push the input timestamp into Player for last-chance save.
  //   * Pre-flip direction when we're inside a mirror-trick segment.
  //   * Dismiss the first-run tutorial on the very first rotate.
  private handleRotate(direction: 1 | -1): void {
    const ctx = this.app.audio.context;
    if (ctx) {
      const now = ctx.currentTime;
      this.player.noteRotateInput(now);
      const verdict = this.beatTiming.judge(now);
      this.score.registerRotationTiming(verdict);
    }
    const dir: 1 | -1 = this.mirrorFx.inverted
      ? ((direction * -1) as 1 | -1)
      : direction;
    this.rotator.rotate(dir);
    this.dismissTutorialIfShown();
  }

  private update(dt: number): void {
    const unscaledDt = this.app.time.unscaledDt;
    if (this.state === "dead") this.deathTimer += unscaledDt;

    // Drive the gate transition on real (unscaled) time so it can restore
    // time.scale even while it is itself dilating it.
    this.gateTransition.update(unscaledDt);
    // FrenzyMode + MusicDirector boost timers also tick on unscaled
    // time so a slow-mo pickup doesn't extend Frenzy duration in
    // real seconds.
    this.frenzy.update(unscaledDt);
    this.musicDirector.update();
    // Push the audio "now" to Player so the last-chance save check
    // inside the upcoming physics.step has a fresh timestamp without
    // Player having to import the audio engine itself.
    const audioCtx = this.app.audio.context;
    if (audioCtx) this.player.syncAudioNow(audioCtx.currentTime);

    if (this.state === "playing") {
      // Endless / daily: keep the streamer's tail topped up so it never
      // exhausts. Cheap O(1) check per frame.
      if (
        this.endlessGenerator &&
        this.streamer.cursorRemaining() < ENDLESS_TOPUP_THRESHOLD
      ) {
        this.streamer.appendEntries(this.endlessGenerator.next());
      }
      // Frenzy speeds up the world scroll without touching the
      // physics dt -- player physics stays canonical. Difficulty
      // speedMul is composed in here ONLY for story mode: the
      // EndlessGenerator already bakes the multiplier into procedural
      // segment.speed values so doubling it on the streamer side
      // would push the world scroll past the BPM ramp's ceiling.
      const diffSpeedMul =
        this.mode === "story" ? this.difficulty.getProfile().speedMul : 1;
      this.streamer.update(dt * this.frenzy.speedMul() * diffSpeedMul);
      this.rotator.update(dt);
      this.streamer.syncBodies();
      this.app.physics.step(dt);
      this.player.update(dt);
    } else if (this.state === "menu") {
      this.streamer.update(dt * 0.4);
      this.rotator.update(dt);
      this.streamer.syncBodies();
    }

    // Trick FX: each driven on UNSCALED dt so a slow-mo doesn't
    // distort their relationship to the beat clock.
    this.pulsePanels.update(unscaledDt);
    this.mirrorFx.update();
    if (this.state === "playing") {
      this.sweepFx.update(unscaledDt);
      // BankingFx fights with the Reverse Gate boss for the camera
      // sustained roll, so skip it while the gate is engaged. The
      // gate itself sets and holds the +PI roll until it ends.
      if (!this.reverseGate.isActive()) this.bankingFx.update();
    }
    // Achievement toasts: fade-out only happens on the timer, so we
    // tick this even on the menu / dead screens. No-op when no toast
    // is visible.
    this.toast.update();
    // Score multiplier driven by Frenzy is read each frame inside
    // ScoreSystem's onSegmentCleared branch via setScoreMultiplier.
    this.score.setScoreMultiplier(this.frenzy.scoreMul());

    // Camera dolly: gate-transition pulls the spring arm slightly closer.
    this.dollyOffset.set(0, 1.4, DOLLY_BASE_Z - this.gateTransition.dollyBoost);
    this.springArm.setOffset(this.dollyOffset);
    this.springArm.update(dt);

    // Camera bank applied AFTER the spring arm so it isn't smoothed away
    // by the slerp toward the look-at quaternion.
    this.cameraBank.apply(this.app.scene.camera, unscaledDt);

    // Camera shake last, so its random offset stacks on the bank.
    const speed = this.streamer.getCurrentSpeed();
    const shakeIntensity =
      this.state === "playing"
        ? Math.min(SHAKE_MAX, SHAKE_BASE + speed * SHAKE_PER_SPEED)
        : 0;
    this.shake.apply(this.app.scene.camera, shakeIntensity);

    // FOV: tween the speed-based base, then layer the gate boost on top
    // by writing the fov directly after the tween for this frame.
    this.fovTween.update(dt);
    if (this.gateTransition.fovBoost > 0.001) {
      this.app.scene.camera.fov += this.gateTransition.fovBoost;
      this.app.scene.camera.updateProjectionMatrix();
    }

    // Audio-driven visuals.
    const audioLow = this.app.audio.lowBandEnergy();
    const audioHigh = this.app.audio.highBandEnergy();
    this.playerPulse.update(unscaledDt, audioLow);
    this.tileHighlight.update(audioLow, this.gateTransition.gateBoost);
    this.sparks.update(unscaledDt);

    // Pickups tick on real time so the slow-mo timer survives the
    // gate-transition's own time-dilation window.
    this.pickups.update(unscaledDt);

    // Sky tunnel: warp speed scales with the streamer speed (so it
    // intensifies as the player runs faster); rotation tracks the
    // worldGroup so the stars roll with the tunnel; pulse tracks the
    // bass for kick-driven brightness flashes.
    const speedSky = this.streamer.getCurrentSpeed();
    const skySpeed01 = math.clamp((speedSky - 8) / 22, 0, 1);
    this.skyTunnel.update(
      unscaledDt,
      this.app.scene.worldGroup.rotation.z,
      skySpeed01,
      audioLow
    );

    // Player trail tracks the ball position and recolors by combo tier.
    // Hidden on menu / dead so the trail doesn't leak into static scenes.
    const trailVisible = this.state === "playing" || this.state === "success";
    this.trail.setVisible(trailVisible);
    if (trailVisible) {
      this.trail.follow(this.player.mesh, unscaledDt, comboTier(this.score.combo));
    }

    // Bloom: audio-EMA base + gate boost, scaled by the current theme's
    // bloom multiplier so different palettes can feel brighter / darker
    // overall.
    const bloomTarget = math.clamp(
      BLOOM_BASE + audioHigh * BLOOM_PER_AUDIO_HIGH,
      0,
      BLOOM_MAX
    );
    this.bloomEMA = math.lerp(this.bloomEMA, bloomTarget, 0.25);
    const bloomMul = this.themes.getBloomMultiplier();
    this.app.postFX.setBloomStrength(
      this.bloomEMA * bloomMul +
        this.gateTransition.bloomBoost +
        this.frenzy.bloomBoost()
    );

    // Chromatic aberration: ramps with speed, peaks during gate, spikes
    // briefly on death.
    const speedT = math.clamp((speed - 10) / 10, 0, 1);
    const ca = CA_BASE + CA_PER_SPEED_T * speedT
      + this.gateTransition.caBoost
      + this.deathCAPulse;
    this.app.postFX.setChromaticAberration(ca);
    this.deathCAPulse = math.lerp(
      this.deathCAPulse,
      0,
      1 - Math.exp(-CA_DEATH_DECAY_LAMBDA * unscaledDt)
    );

    // HUD updates last (real-time so they stay smooth even in slow-mo).
    this.hud.updateDistanceTween(this.streamer.getDistance(), unscaledDt);
    this.hud.setScore(this.score.score, unscaledDt);
  }

  dispose(): void {
    this.frenzy.dispose();
    this.pulsePanels.dispose();
    this.sweepFx.dispose();
    this.beatTiming.dispose();
    this.musicDirector.dispose();
    this.pickups.dispose();
    this.reverseGate.dispose();
    this.achievements.dispose();
    this.toast.clear();
    this.beat.dispose();
    this.streamer.dispose();
    this.factory.dispose();
    this.player.dispose();
    this.inputMap.dispose();
    this.sparks.dispose();
    this.trail.dispose();
    this.skyTunnel.dispose();
  }
}
